import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Button, Group, Radio, Select, Table, Text, TextInput } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, costsApi, type CostPlan, type CostVariantInput, undoPaths } from '../../api';
import { formatPercent } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { EditableText } from '../../ui/EditableText';
import { Section } from '../../ui/Section';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';

/**
 * The variants of the plan. Kept deliberately plain: a code, a name, a line of
 * description, risk and range, and which one cost control uses. What a
 * variant changes lives where it belongs - in the values and the lines.
 */
export function VariantsPanel({ projectId, plan, canEdit, onClose, onCreated }: {
  projectId: string;
  plan: CostPlan;
  canEdit: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['costs', projectId] });
  const undo = useUndo();
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & CostVariantInput) => {
      const before = plan.variants.find((v) => v.id === id);
      const { isPrimary: _p, ...rest } = body;
      void _p;
      const step = before && Object.keys(rest).length > 0
        ? guardedStep(`Variante ${before.code}`, 'PATCH', undoPaths.costVariant(projectId, id), inversePatch(rest, before as unknown as Record<string, unknown>), rest)
        : null;
      return undo.recordAfter(costsApi.updateVariant(projectId, id, body), step);
    },
    onSettled: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => costsApi.removeVariant(projectId, id), onSettled: refresh });
  const updatePlan = useMutation({ mutationFn: (body: { riskPercent?: number; rangePercent?: number }) => costsApi.updatePlan(projectId, body), onSettled: refresh });
  const add = useMutation({
    mutationFn: (body: { code: string; name: string; copyFrom: string | null }) => costsApi.addVariant(projectId, body),
    onSuccess: (r) => { setDraft({ code: '', name: '', copyFrom: null }); onCreated(r.id); },
    onSettled: refresh,
  });
  const next = `V${plan.variants.length + 1}`;
  const [draft, setDraft] = useState<{ code: string; name: string; copyFrom: string | null }>({ code: '', name: '', copyFrom: null });
  const error = update.error ?? remove.error ?? add.error ?? updatePlan.error;
  const percent = (v: number | null): number => Math.min(100, Math.max(0, v ?? 0));

  return (
    <Section
      title="Varianten"
      description="Mehrere Konzepte einer Kostenermittlung nebeneinander. Gliederung, Positionen und Werte teilen sie sich; je Variante unterscheiden sich nur die Werte, die dort eigens eingetragen sind, und Positionen, die nur in einer Variante stehen."
      actions={<Button size="xs" variant="subtle" color="gray" onClick={onClose}>Schließen</Button>}
    >
      {error && <Alert color="red" py={6} withCloseButton onClose={() => [update, remove, add, updatePlan].forEach((m) => m.reset())}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}
      {plan.variants.length === 0 ? (
        <Group gap="lg">
          <Text size="sm" c="dimmed">Noch keine Varianten – die Kostenplanung rechnet als eine.</Text>
          <Group gap={6} wrap="nowrap">
            <Text size="sm">Risikozuschlag</Text>
            <div style={{ width: 70 }}><AmountCell value={plan.riskPercent} digits={0} readOnly={!canEdit} onCommit={(v) => updatePlan.mutate({ riskPercent: percent(v) })} /></div>
            <Text size="sm">%, Bandbreite ±</Text>
            <div style={{ width: 70 }}><AmountCell value={plan.rangePercent} digits={0} readOnly={!canEdit} onCommit={(v) => updatePlan.mutate({ rangePercent: percent(v) })} /></div>
            <Text size="sm">%</Text>
          </Group>
        </Group>
      ) : (
        // A fixed grid keeps long names and descriptions inside the frame (full text in the tooltip);
        // on a narrow screen the table scrolls inside the frame instead of squeezing a column away.
        <Table.ScrollContainer minWidth={820}>
        <Table verticalSpacing={3} className="pst-tabular" layout="fixed">
          <Table.Thead>
            <Table.Tr>
              {canEdit && <Table.Th w={30} />}
              <Table.Th w={80}>Kürzel</Table.Th>
              <Table.Th w={220}>Bezeichnung</Table.Th>
              <Table.Th>Beschreibung</Table.Th>
              <Table.Th w={90} ta="right" title="Zuschlag auf die Summe der Kostengruppen" style={{ whiteSpace: 'nowrap' }}>Risiko (%)</Table.Th>
              <Table.Th w={130} ta="right" title="Genauigkeit der Ermittlung, als Spanne um die Summe mit Risiko" style={{ whiteSpace: 'nowrap' }}>Bandbreite ± (%)</Table.Th>
              <Table.Th w={140} pl="lg" title="Gegen diese Variante laufen Kostenkontrolle und Vergabe" style={{ whiteSpace: 'nowrap' }}>Kostenkontrolle</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {plan.variants.map((v) => (
              <Table.Tr key={v.id}>
                {canEdit && (
                  <Table.Td>
                    <ActionIcon
                      size="xs" variant="subtle" color="gray" aria-label={`Variante ${v.code} löschen`} title="Variante löschen – mit ihren eigenen Positionen und Werten"
                      onClick={() => { if (window.confirm(`Variante ${v.code} löschen? Positionen, die nur in dieser Variante stehen, und ihre eigenen Werte gehen dabei verloren.`)) remove.mutate(v.id); }}
                    >✕</ActionIcon>
                  </Table.Td>
                )}
                <Table.Td><EditableText value={v.code} bold monospace onCommit={(code) => canEdit && update.mutate({ id: v.id, code })} /></Table.Td>
                <Table.Td><EditableText value={v.name} title={v.name} onCommit={(name) => canEdit && update.mutate({ id: v.id, name })} /></Table.Td>
                <Table.Td><EditableText value={v.description ?? ''} title={v.description || 'Zum Ändern anklicken'} size="xs" dimmed allowEmpty placeholder="z. B. Stand, Preisstand, Reifegrad" onCommit={(description) => canEdit && update.mutate({ id: v.id, description: description || null })} /></Table.Td>
                <Table.Td><AmountCell value={v.riskPercent} digits={0} readOnly={!canEdit} onCommit={(riskPercent) => update.mutate({ id: v.id, riskPercent: percent(riskPercent) })} /></Table.Td>
                <Table.Td><AmountCell value={v.rangePercent} digits={0} readOnly={!canEdit} onCommit={(rangePercent) => update.mutate({ id: v.id, rangePercent: percent(rangePercent) })} /></Table.Td>
                <Table.Td pl="lg">
                  <Radio size="xs" checked={v.isPrimary} disabled={!canEdit} onChange={() => update.mutate({ id: v.id, isPrimary: true })} label={v.isPrimary ? 'diese' : undefined} aria-label={`Kostenkontrolle gegen ${v.code}`} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
      )}
      {canEdit && (
        <form onSubmit={(e) => { e.preventDefault(); add.mutate({ code: draft.code.trim() || next, name: draft.name.trim() || draft.code.trim() || next, copyFrom: draft.copyFrom }); }}>
          <Group gap="xs" align="flex-end" wrap="wrap">
            <TextInput size="xs" label="Kürzel" placeholder={next} value={draft.code} onChange={(e) => { const code = e.currentTarget.value; setDraft((d) => ({ ...d, code })); }} w={80} />
            <TextInput size="xs" label="Bezeichnung" placeholder="z. B. Container" value={draft.name} onChange={(e) => { const name = e.currentTarget.value; setDraft((d) => ({ ...d, name })); }} w={220} />
            {plan.variants.length > 0 && (
              <Select
                size="xs" label="Ausgangspunkt" w={220} allowDeselect={false}
                value={draft.copyFrom ?? ''}
                onChange={(v) => setDraft((d) => ({ ...d, copyFrom: v || null }))}
                data={[{ value: '', label: 'gemeinsame Werte' }, ...plan.variants.map((v) => ({ value: v.id, label: `Kopie von ${v.code}` }))]}
              />
            )}
            <Button size="xs" type="submit" variant="light" loading={add.isPending}>Variante anlegen</Button>
          </Group>
          <Text size="xs" c="dimmed" mt={4}>
            {plan.variants.length === 0
              ? 'Die erste Variante übernimmt alles Bisherige. Danach eine zweite anlegen und dort nur die abweichenden Werte eintragen.'
              : `Bisher: ${plan.variants.map((v) => `${v.code} ${formatPercent(v.riskPercent)} Risiko`).join(', ')}.`}
          </Text>
        </form>
      )}
    </Section>
  );
}
